import artSpec from "../art/art-spec.json";
import { SCENE_HEIGHT, SCENE_WIDTH, drawTankScene, type Canvas2D } from "./draw";

export function pal(ramp: keyof typeof artSpec.masterPalette, index: number): string {
  const colour = artSpec.masterPalette[ramp][index];
  if (!colour) {
    throw new Error(`Missing master palette colour ${ramp}[${index}]`);
  }
  return colour;
}

const INK = pal("ink", 0);
const INK_SHADE = pal("ink", 1);

function px(ctx: Canvas2D, x: number, y: number, w: number, h: number, colour: string): void {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  if (ix < 0 || iy < 0 || ix >= w || iy >= h) return;
  ctx.fillStyle = colour;
  ctx.fillRect(ix, iy, 1, 1);
}

function drawDitheredBands(
  ctx: Canvas2D,
  y0: number,
  y1: number,
  w: number,
  h: number,
  cols: readonly string[],
): void {
  const n = cols.length;
  const hh = (y1 - y0) / n;
  for (let y = y0; y < y1; y++) {
    const i = Math.min(n - 1, Math.floor((y - y0) / hh));
    ctx.fillStyle = cols[i]!;
    ctx.fillRect(0, y, w, 1);
  }
  for (let i = 1; i < n; i++) {
    const sy = Math.round(y0 + i * hh);
    for (let x = 0; x < w; x++) {
      if ((x + sy) % 2 === 0) {
        px(ctx, x, sy, w, h, cols[i]!);
      } else {
        px(ctx, x, sy - 1, w, h, cols[i - 1]!);
      }
      if ((x + sy) % 2 === 1 && sy + 1 < y1) {
        px(ctx, x, sy + 1, w, h, cols[i]!);
      }
    }
  }
}

function bmp(
  ctx: Canvas2D,
  rows: readonly string[],
  palette: Record<string, string>,
  x: number,
  y: number,
): void {
  for (let r = 0; r < rows.length; r++) {
    const row = rows[r]!;
    for (let c = 0; c < row.length; c++) {
      const ch = row[c]!;
      if (ch === ".") continue;
      const colour = palette[ch];
      if (colour) {
        ctx.fillStyle = colour;
        ctx.fillRect(x + c, y + r, 1, 1);
      }
    }
  }
}

const SUNSET_COLS = {
  water: [
    pal("purple", 1),
    pal("purple", 2),
    pal("pink", 1),
    pal("orange", 1),
    pal("orange", 2),
    pal("teal", 1),
  ],
  ray: pal("orange", 3),
  sandLight: pal("sand", 1),
  sandDark: pal("sand", 0),
  weed: [pal("green", 0), pal("green", 1), pal("green", 2)],
};

const NIGHT_COLS = {
  water: [
    pal("ink", 3),
    pal("purple", 0),
    pal("blue", 0),
    pal("blue", 0),
    pal("teal", 0),
    pal("teal", 0),
  ],
  star: pal("gold", 4),
  ray: pal("blue", 1),
  sandLight: pal("sand", 0),
  sandDark: pal("neutral", 0),
  weed: [pal("green", 0), pal("green", 1), pal("green", 0)],
};

function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

const WEED_POSITIONS = [
  [6, 44],
  [11, 45],
  [70, 44],
  [74, 45],
  [64, 46],
] as const;

export function drawBackground(
  ctx: Canvas2D,
  index: number,
  tick = 0,
  reduced = false,
): void {
  const w = SCENE_WIDTH;
  const h = SCENE_HEIGHT;
  const t = reduced ? 0 : Math.abs(Math.trunc(tick));

  if (index <= 0) {
    drawTankScene(ctx, w, h, { tick: t, reducedMotion: reduced });
    return;
  }

  const cols = index === 1 ? SUNSET_COLS : NIGHT_COLS;
  ctx.clearRect?.(0, 0, w, h);

  const sandY = 44;
  drawDitheredBands(ctx, 0, sandY, w, h, cols.water);

  if (index === 1) {
    // Warm light rays
    for (let i = 0; i < 4; i++) {
      const rx = 8 + i * 20 + (reduced ? 0 : Math.round(Math.sin(t * 0.25 + i) * 3));
      for (let y = 0; y < 26; y++) {
        const rw = 1 + Math.floor(y / 9);
        for (let x = 0; x < rw; x++) {
          if ((x + y) % 2 === 0) {
            px(ctx, rx + Math.floor(y / 3) + x, y, w, h, cols.ray);
          }
        }
      }
    }
  } else {
    // Night stars and plankton dots
    const stars = [
      [10, 6],
      [22, 12],
      [36, 5],
      [52, 14],
      [68, 7],
      [16, 22],
      [44, 20],
      [62, 24],
    ] as const;
    stars.forEach(([sx, sy], i) => {
      if (reduced || (t + i) % 3 !== 0) {
        px(ctx, sx, sy, w, h, NIGHT_COLS.star);
      }
    });
  }

  // Sandy floor
  for (let x = 0; x < w; x++) {
    const sh = Math.round(sandY - Math.sin(x / 8) * 1.5);
    for (let y = sh; y < h; y++) {
      px(ctx, x, y, w, h, y < sh + 3 ? cols.sandLight : cols.sandDark);
    }
  }

  const r = rng(11);
  for (let i = 0; i < 26; i++) {
    const pxX = Math.floor(r() * 78) + 1;
    const pxY = 48 + Math.floor(r() * 10);
    px(ctx, pxX, pxY, w, h, cols.sandDark);
    px(ctx, pxX + 1, pxY, w, h, cols.sandDark);
  }

  // Seaweed
  WEED_POSITIONS.forEach(([bx, by], j) => {
    for (let k = 0; k < 10; k++) {
      const sw = reduced ? 0 : Math.round(Math.sin(t * 0.65 + j + k / 3) * 1.4);
      px(ctx, bx + sw, by - k, w, h, cols.weed[(k + j) % cols.weed.length]!);
      if (k % 3 === 1) {
        px(ctx, bx + sw + 1, by - k, w, h, cols.weed[1]!);
      }
    }
  });

  // Bubbles
  if (!reduced) {
    for (let i = 0; i < 5; i++) {
      const by = Math.round(sandY - (((t * 4 + i * 17) % sandY + sandY) % sandY));
      const bx = 14 + i * 14 + Math.round(Math.sin(t * 0.9 + i) * 2);
      px(ctx, bx, by, w, h, pal("cream", 3));
      if (i % 2 === 1) {
        px(ctx, bx + 1, by, w, h, cols.ray);
      }
    }
  }
}

/* --- Decorations (Zone: left 4%..30%, top 35%..95% -> x: [3, 24], y: [21, 57]) --- */

const CORAL_ROWS = [
  "......oo......",
  ".....oppo.oo..",
  "..oo.oppo.opo.",
  ".oppooppooppo.",
  ".opPppppppPpo.",
  ".opppppppppp..",
  "..oppoppoppo..",
  "..oppoopppo...",
  "...opppppppo..",
  "...opppppppo..",
  "....opppppo...",
  "....opppppo...",
  "....opppppo...",
  "...oopppppgoo.",
  "..oggpppppggo.",
  "..ooooooooooo.",
] as const;

const CHEST_ROWS = [
  "...oooooooo...",
  "..owggwwggwo..",
  ".owwggwwwwggo.",
  "owwwggwwwwggwo",
  "oooooooooooooo",
  "owggwwoowwggwo",
  "owggwwGGwwggwo",
  "owggwwoowwggwo",
  "owggwwwwwwggwo",
  "owggwwwwwwggwo",
  "oooooooooooooo",
  ".oooooooooooo.",
] as const;

const ANCHOR_ROWS = [
  "....ooooo....",
  "...oohhhooo..",
  "...oh.o.hho..",
  "...oohhhooo..",
  ".....ohho....",
  "..ooooooooo..",
  "..ohhhhhhho..",
  "..ooooooooo..",
  ".....ohho....",
  ".....ohho....",
  "o....ohho...o",
  "oho..ohho..oho",
  "ohho.ohho.ohho",
  ".ohhoohhoohho.",
  "..ohhhhhhhho..",
  "...oooooooo...",
  "....oooooo...",
] as const;

export function drawDecoration(ctx: Canvas2D, index: number): void {
  if (index < 0) return;
  if (index === 0) {
    // Coral
    bmp(
      ctx,
      CORAL_ROWS,
      {
        o: INK,
        p: pal("pink", 1),
        P: pal("pink", 0),
        g: pal("green", 1),
      },
      7,
      38,
    );
  } else if (index === 1) {
    // Treasure chest
    bmp(
      ctx,
      CHEST_ROWS,
      {
        o: INK,
        w: pal("umber", 2),
        g: pal("gold", 3),
        G: pal("gold", 4),
      },
      7,
      43,
    );
  } else if (index === 2) {
    // Anchor
    bmp(
      ctx,
      ANCHOR_ROWS,
      {
        o: INK,
        h: pal("neutral", 2),
      },
      8,
      37,
    );
  }
}

/* --- Toys (Zone: left 26%..40%, top 81%..98% -> x: [20, 32], y: [48, 59]) --- */

const BALL_ROWS = [
  "..oooo..",
  ".orhrrro",
  "orrrrrro",
  "owwwwwwg",
  "owwwwwwg",
  "orrrrrrg",
  ".orrrrgo",
  "..oooo..",
] as const;

const HOOP_ROWS = [
  "..oooooo..",
  ".ottddtto.",
  "otd....dto",
  "od......do",
  "otd....dto",
  ".ottddtto.",
  "..oooooo..",
] as const;

const DUCK_ROWS = [
  "...oooo..",
  "..oYYYYo.",
  "OOoYkYYYo",
  ".oYYYYYYo",
  "oYYYyyYYo",
  ".oYYYYYo.",
  "..ooooo..",
  "..O.O....",
] as const;

export function drawToy(ctx: Canvas2D, index: number): void {
  if (index < 0) return;
  if (index === 0) {
    // Ball
    bmp(
      ctx,
      BALL_ROWS,
      {
        o: INK,
        r: pal("red", 2),
        h: pal("pink", 1),
        w: pal("cream", 3),
        g: pal("red", 0),
      },
      22,
      50,
    );
  } else if (index === 1) {
    // Hoop
    bmp(
      ctx,
      HOOP_ROWS,
      {
        o: INK,
        t: pal("teal", 2),
        d: pal("teal", 3),
      },
      21,
      51,
    );
  } else if (index === 2) {
    // Rubber duck
    bmp(
      ctx,
      DUCK_ROWS,
      {
        o: INK,
        Y: pal("gold", 4),
        y: pal("gold", 3),
        O: pal("orange", 2),
        k: INK_SHADE,
      },
      22,
      50,
    );
  }
}

/* --- Companions (Zone: left 69%..98%, top 36%..97% -> x: [55, 79], y: [21, 58]) --- */

const FISH_ROWS_0 = [
  "..ooooo....",
  ".ofhhffo...",
  "oefkffhfo..",
  ".offhhhhfoo",
  "..ofhhffofo",
  "...ooooo.oo",
  "..........o",
] as const;

const FISH_ROWS_1 = [
  "..........o",
  "...ooooo.oo",
  "..ofhhffofo",
  ".offhhhhfoo",
  "oefkffhfo..",
  ".ofhhffo...",
  "..ooooo....",
] as const;

const CRAB_ROWS_0 = [
  ".oo......oo.",
  "orro.oo.orro",
  ".orrrwwrrro.",
  "..orrrrrro..",
  ".oorroo.oor.",
  "o.o.o..o.o.o",
  ".o.o....o.o.",
] as const;

const CRAB_ROWS_1 = [
  ".oo......oo.",
  "orro.oo.orro",
  ".orrrwwrrro.",
  "..orrrrrro..",
  "ro.oorro.oor",
  ".o.o.o..o.o.",
  "o.o......o.o.",
] as const;

const TURTLE_ROWS_0 = [
  "...oooooo......",
  "..oghhhhgo.ooo.",
  ".oghhhhhhgotko.",
  "ooghhsshhgoooo.",
  "ogghsssshhgo...",
  "ooghhsshhgoooo.",
  ".oghhhhhhgotko.",
  "..oghhhhgo.ooo.",
  "...oooooo......",
] as const;

const TURTLE_ROWS_1 = [
  ".....oooooo....",
  "....oghhhhgo...",
  ".oooghhhhhhgo..",
  "otkoghhsshhgooo",
  "ooogghsssshhgo.",
  "otkoghhsshhgooo",
  ".oooghhhhhhgo..",
  "....oghhhhgo...",
  ".....oooooo....",
] as const;

export function drawCompanion(
  ctx: Canvas2D,
  index: number,
  tick = 0,
  reduced = false,
): void {
  if (index < 0) return;
  const t = reduced ? 0 : Math.abs(Math.trunc(tick));
  const f = t % 2;

  if (index === 0) {
    // Little fish (swims at y ~ 32)
    bmp(
      ctx,
      f === 0 ? FISH_ROWS_0 : FISH_ROWS_1,
      {
        o: INK,
        f: pal("orange", 1),
        h: pal("orange", 2),
        e: pal("cream", 3),
        k: INK_SHADE,
      },
      64,
      32,
    );
  } else if (index === 1) {
    // Crab (walks on sand at y ~ 49)
    bmp(
      ctx,
      f === 0 ? CRAB_ROWS_0 : CRAB_ROWS_1,
      {
        o: INK,
        r: pal("red", 2),
        w: pal("cream", 3),
      },
      63,
      49,
    );
  } else if (index === 2) {
    // Turtle (glides at y ~ 35)
    bmp(
      ctx,
      f === 0 ? TURTLE_ROWS_0 : TURTLE_ROWS_1,
      {
        o: INK,
        g: pal("green", 1),
        h: pal("green", 2),
        s: pal("green", 0),
        t: pal("teal", 1),
        k: INK_SHADE,
      },
      60,
      35,
    );
  }
}
